import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useSearchParams } from "react-router";
import { z } from "zod";
import { authErrorMessage, useAuth } from "@/auth/AuthProvider";
import { ErrorPanel } from "@/components/ErrorPanel";
import { Spinner } from "@/components/Spinner";
import { AuthLayout, FieldError } from "./AuthLayout";
import { usePostAuthRedirect } from "./usePostAuthRedirect";

const schema = z.object({
  email: z
    .string()
    .trim()
    .min(1, "Enter your email.")
    .email("That doesn't look like an email address."),
  password: z.string().min(1, "Enter your password."),
});
type Values = z.infer<typeof schema>;

export function SignInPage() {
  const { ready, signIn } = useAuth();
  const [params] = useSearchParams();
  const { redirecting, userError, retry } = usePostAuthRedirect("/start");
  const [formError, setFormError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { email: "", password: "" },
  });

  const onSubmit = handleSubmit(async ({ email, password }) => {
    setFormError(null);
    try {
      await signIn(email, password);
    } catch (error) {
      setFormError(authErrorMessage(error));
    }
  });

  const query = params.toString() ? `?${params.toString()}` : "";

  return (
    <AuthLayout title="auth.signin.h1" sub="auth.signin.sub" testId="signin-page">
      {!ready ? (
        <Spinner label="Loading" />
      ) : redirecting ? (
        userError ? (
          <ErrorPanel error={userError} onRetry={() => retry()} />
        ) : (
          <Spinner label="Opening your plan" />
        )
      ) : (
        <form onSubmit={onSubmit} noValidate>
          <label className="wp-field">
            Email
            <input
              type="email"
              autoComplete="email"
              {...register("email")}
              aria-invalid={!!errors.email}
              data-testid="signin-email"
            />
            <FieldError message={errors.email?.message} />
          </label>
          <label className="wp-field">
            Password
            <input
              type="password"
              autoComplete="current-password"
              {...register("password")}
              aria-invalid={!!errors.password}
              data-testid="signin-password"
            />
            <FieldError message={errors.password?.message} />
          </label>
          {formError && (
            <p className="wp-form-err" role="alert" data-testid="signin-error">
              {formError}
            </p>
          )}
          <button
            type="submit"
            className="wp-btn"
            disabled={isSubmitting}
            data-testid="signin-submit"
          >
            {isSubmitting ? "Signing in…" : "Sign in"}
          </button>
          <div className="wp-auth-links">
            <Link to={`/forgot-password${query}`}>Forgot your password?</Link>
            <span>
              New here? <Link to={`/signup${query}`}>Create an account</Link>
            </span>
          </div>
        </form>
      )}
    </AuthLayout>
  );
}
